<template>
  <div
    class="
      navbar mx-auto flex max-w-full px-4 font-sans
      sm:px-6
      lg:px-8
    ">
    <div class="left-menu">
      <RouterLink to="/applications">
        <!--        <img-->
        <!--          class="hidden md:flex logo"-->
        <!--          :src="require('@/assets/'+ theme +'/images/logo.svg')"-->
        <!--          alt="Company logo"-->
        <!--        >-->
        <img
          class="logo p-2"
          :src="logo"
          alt="Company logo"
        />
      </RouterLink>
      <DropdownMenu class="hover-effect no-print">
        <DropdownMenuTrigger>
          <div class="dropdown-arrow"></div>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <RouterLink
            v-permission="['factory-dashboard']"
            to="/dashboard"
          >
            <DropdownMenuItem>
              {{ t('navbar.dashboard') }}
            </DropdownMenuItem>
          </RouterLink>
          <RouterLink
            v-permission="['factory-studio']"
            to="/studio/regions"
          >
            <DropdownMenuItem>
              {{ t('navbar.studio') }}
            </DropdownMenuItem>
          </RouterLink>

          <RouterLink
            v-permission="['performance-dashboard']"
            to="/performance-dashboard"
          >
            <DropdownMenuItem>
              {{ t('navbar.performanceDashboard') }}
            </DropdownMenuItem>
          </RouterLink>
          <RouterLink
            v-permission="['public-api-dashboard']"
            to="/public-api-dashboard"
          >
            <DropdownMenuItem>
              {{ t('navbar.publicApiDashboard') }}
            </DropdownMenuItem>
          </RouterLink>
          <RouterLink
            v-permission="['logs']"
            to="/logs"
          >
            <DropdownMenuItem>
              {{ t('navbar.logs') }}
            </DropdownMenuItem>
          </RouterLink>
          <RouterLink to="/alerts">
            <DropdownMenuItem data-testid="navbar-link-alerts">
              {{ t('navbar.alerts') }}
            </DropdownMenuItem>
          </RouterLink>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <div class="centered-menu no-print">
      <RouterView
        class="navbar-router-view"
        name="navbar"
      />
    </div>

    <div class="right-menu no-print gap-1">
      <div
        v-if="hasAiModule"
        class="right-menu-item hover-effect flex h-full items-center px-2"
        :title="t('ai.title')"
        @click="aiStore.togglePanel()"
      >
        <Sparkles
          :class="['size-4', aiStore.isOpen && 'text-primary']"
        />
      </div>
      <LangSelect :title="t('navbar.languages')" />
      <div
        v-if="hasWatcherPermission"
        class="right-menu-item hover-effect h-full items-center"
        :title="t('navbar.watchers')"
      >
        <WatcherObserver>
          <RouterLink
            v-slot="{ href, navigate }"
            custom
            to="/watcher"
            class="h-full"
          >
            <a
              :href="href"
              class="flex items-center px-2"
              @click="navigate"
            >
              <div class="image-item relative">
                <Bell class="size-4" />
                <p
                  v-if="watchersAlertCount"
                  class="notification-number"
                >
                  {{ watchersAlertCount }}
                </p>
              </div>
            </a>
          </RouterLink>
        </WatcherObserver>
      </div>
      <DropdownMenu
        v-if="
          tenant.attributes.support_navbar_dropdown &&
            tenant.attributes.support_navbar_dropdown.length
        "
      >
        <DropdownMenuTrigger>
          <div class="right-menu-item hover-effect flex items-center px-2">
            <HelpCircle class="size-4" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            v-for="item in tenant.attributes.support_navbar_dropdown"
            :key="item.link"
          ><a
            :href="item.link"
            target="_blank"
            class="d-flex align-items-center"
          >
            <i
              v-if="item.icon"
              class="d-flex dropdown-icon"
              :class="'i-' + item.icon"
            ></i>
            {{ getValueByLang(item.name) }}
          </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu class="w-56">
        <DropdownMenuTrigger data-testid="user-menu">
          <div class="ml-4 flex items-center gap-2">
            <div class="flex size-10 items-center justify-center rounded-full">
              <Avatar class="size-10">
                <AvatarFallback>{{ getValueByLang(tenant.name)[0] }}</AvatarFallback>
              </Avatar>
            </div>

            <div
              class="
                hidden flex-col items-start
                xl:flex
              ">
              <div class="text-base font-medium">
                {{ getValueByLang(tenant.name) }}
              </div>
              <div class="text-sm font-medium text-muted-foreground">
                {{ me.email }}
              </div>
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent data-testid="user-menu-content">
          <DropdownMenuLabel>
            <div class="flex flex-col items-start">
              <div class="text-base font-medium">
                {{ getValueByLang(tenant.name) }}
              </div>
              <div class="text-sm font-medium text-muted-foreground">
                {{ me.email }}
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem v-if="hasSettingsPermission">
            <RouterLink :to="{ name: 'ChangePassword' }">
              <div class="flex items-center justify-between gap-2">
                <Settings class="size-4" />
                <span>{{ t('navbar.settings') }}</span>
              </div>
            </RouterLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator v-if="hasSettingsPermission" />
          <DropdownMenuItem
            data-testid="logout-button"
            @click="logout"
          >
            <div class="flex items-center justify-between gap-2">
              <LogOut class="size-4" />
              <span>{{ t('navbar.logOut') }}</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { Bell, HelpCircle, LogOut, Settings, Sparkles } from 'lucide-vue-next'

import LangSelect from '@/components/LangSelect/index.vue'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import WatcherObserver from '@/components/Watcher/watcher-observer.vue'
import { useLang } from '@/composables/useLang'
import { useAiStore } from '@/stores/ai'
import { useAppStore } from '@/stores/app'
import { useCoreStore } from '@/stores/core'
import { useUserStore } from '@/stores/user'
import { IWatcher } from '@/types/shared'

// Composables
const route = useRoute()
const aiStore = useAiStore()
const userStore = useUserStore()
const coreStore = useCoreStore()
const appStore = useAppStore()
const { getValueByLang } = useLang()
const { t } = useI18n()

// State
const selected = ref('')

// Computed
const theme = appStore.theme
const tenant = userStore.tenant
const me = userStore.me
const hasAiModule = userStore.hasAiModule
const hasWatcherPermission = userStore.hasWatcherPermission
const watchersAlertCount = computed(
  () => coreStore.watchers.filter((watcher: IWatcher) => watcher.alert).length,
)
const hasSettingsPermission = computed(() => userStore.hasSettingsPermission)
const logo = computed(
  () => new URL(`/src/assets/${theme}/images/only-logo.svg`, import.meta.url).href,
)
const userAvatar = computed(() => new URL('/src/assets/images/user.png', import.meta.url).href)

// Methods
const logout = async () => {
  await userStore.logOut()
}

const onUrlChange = () => {
  if (route.fullPath.endsWith('logs/')) {
    selected.value = 'logs'
  } else {
    selected.value = ''
  }
}

// Lifecycle hooks
onMounted(() => {
  onUrlChange()
})

// Watch
watch(
  () => route,
  () => onUrlChange(),
  { immediate: true, deep: true },
)
</script>

<style lang="scss">
.navbar {
  @apply h-16;

  .left-menu {
    @apply flex;
    line-height: var(--nav-height);

    .logo {
      height: var(--nav-height);
      max-width: 200px;

      &:hover {
        cursor: pointer;
      }
    }

    .dropdown-arrow {
      height: var(--nav-height);
      margin-left: 15px;
      width: 25px;
      background-image: url('../../../assets/images/dropdown.svg');
      background-size: contain;
      background-repeat: no-repeat;

      &:hover {
        opacity: 0.75;
        cursor: pointer;
      }
    }
  }

  .hamburger-container {
    line-height: 46px;
    height: 100%;
    float: left;
    padding: 0 15px;
    cursor: pointer;
    transition: background 0.3s;
    -webkit-tap-highlight-color: transparent;

    &:hover {
      background: rgba(0, 0, 0, 0.025);
    }
  }

  .breadcrumb-container {
    float: left;
  }

  .errLog-container {
    display: inline-block;
    vertical-align: top;
  }

  .centered-menu {
    margin: 0 32px;
    height: 100%;
    display: flex;
    flex: 1;
    @apply text-muted-foreground;

    .centered-menu-item {
      padding: 0px 10px;
      text-align: center;
      cursor: pointer;

      p.item-text {
        font-size: 10px;
        margin-bottom: 0;
      }

      .image-item {
        width: 45px;
        height: 35px;
        border-radius: 4px;
        display: inline-flex;
        border: solid 1px;

        background: hsl(var(--sidebar-accent));
        border-color: hsl(var(--sidebar-accent));

        img,
        i {
          vertical-align: middle;
          margin: 0 auto;
          line-height: 12px;
          filter: brightness(0) invert(1);
          height: 33px;
          padding: 10px;
          width: 45px;
        }
      }

      &:hover .image-item {
        background: hsl(var(--sidebar-foreground));
        border: solid 1px #333c43;
      }
    }

    .active .image-item {
      background: hsl(var(--sidebar-foreground));
      border: solid 1px #333c43;
    }
  }

  .right-menu {
    @apply flex;
    height: 100%;
    line-height: var(--nav-height);

    &:focus {
      outline: none;
    }

    .alert-container {
      visibility: hidden;
      opacity: 0;
      transition: 0.5s;
    }

    .active .alert-container {
      visibility: visible;
      opacity: 1;
      transition: 0.5s;
    }

    .right-menu-item {
      height: 100%;
      font-size: 18px;
      vertical-align: text-bottom;

      .notification-image {
        vertical-align: middle;
      }

      &.hover-effect {
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background: rgba(0, 0, 0, 0.025);
        }
      }

      &.active {
        background: rgba(0, 0, 0, 0.025);
      }
    }

    .avatar-container {
      .avatar-wrapper {
        position: relative;
        display: flex;
        height: 100%;
        align-items: center;

        .user-avatar {
          cursor: pointer;
          width: 30px;
          height: 30px;
          border-radius: 10px;
          vertical-align: middle;
          margin-right: 10px;
        }

        .user-wrapper {
          display: flex;
          flex-direction: column;

          .top,
          .bottom {
            line-height: 1.2rem;
          }

          .top {
            font-size: 1.2rem;
          }

          .bottom {
            font-size: 0.8rem;
          }
        }
      }
    }
  }

  .notification-wrapper {
    position: relative;
    @apply px-2;
  }

  .notification-number {
    font-size: 10px;
    background-color: #eb5d40;
    border-radius: 12px;
    padding: 2px 4px;
    color: #ffffff;
    min-height: 10px;
    min-width: 10px;
    position: absolute;
    top: -15px;
    right: -5px;
    line-height: initial;
  }
}

.dropdown-icon {
  margin-right: 0.5rem !important;

  &:before {
    font-size: 24px;
  }
}
</style>
